import { useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { StoryText } from '@/components/microstory/text';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import { useLingui } from '@lingui/react';

type MentalBurdensChapter1Props = {
  data: MentalBurdensData;
};

export function MentalBurdensChapter2(props: MentalBurdensChapter1Props) {
  const { data } = props;

  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const [chartData, setChartData] = useState<SunriseChartData>();
  const handleStepEnter = (e: OnStepEnterExitArgs<SunriseChartData>) => {
    if (e.data) {
      setChartData(e.data);
    }
  };

  const section1ChartData1 = {
    label: t({
      message: '% reporting often feeling depressed or having little interest in doing things',
      id: 'mental-burdens.chapter-2.chart-top-label',
    }),
    data: [
      {
        label: youngerGeneration(i18n),
        value: data.often_depressed.younger,
      },
    ],
  };

  const section1ChartData2 = {
    data: [
      {
        label: olderGeneration(i18n),
        value: data.often_depressed.older,
      },
    ],
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <SunriseChart data={chartData} formatValue={formatPercentage} />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter} offset={0.7} hasGraphic>
        <ScrollStep data={[section1ChartData1]}>
          <StoryText>
            <Trans id="mental-burdens.chapter-2.text-1">
              On average, {formatPercentage(data.often_depressed.younger)} of{' '}
              <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> say they frequently feel
              depressed or have little interest in doing things.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={[section1ChartData1, section1ChartData2]}>
          <StoryText>
            <Trans id="mental-burdens.chapter-2.text-2">
              That compares to {formatPercentage(data.often_depressed.older)} of{' '}
              <SecondaryAnnotatedType>older generations.</SecondaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
